import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Coffee01Icon as Coffee, FavouriteIcon as Heart, Copy01Icon as Copy, Tick01Icon as Check, Cancel01Icon as X, LinkSquare01Icon as ExternalLink } from 'hugeicons-react'

interface DonateModalProps {
    isOpen: boolean
    onClose: () => void
}

export default function DonateModal({ isOpen, onClose }: DonateModalProps) {
    const [copiedField, setCopiedField] = useState<string | null>(null)
    const [selectedTier, setSelectedTier] = useState<number>(5)

    if (!isOpen) return null

    const handleCopy = (text: string, label: string) => {
        navigator.clipboard.writeText(text)
        setCopiedField(label)
        setTimeout(() => setCopiedField(null), 2000)
    }

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/80 backdrop-blur-md">
                {/* Backdrop Click */}
                <div className="absolute inset-0" onClick={onClose} />

                {/* Bottom to Top Full Page Opener Window Dialog */}
                <motion.div
                    initial={{ y: '100%', opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: '100%', opacity: 0 }}
                    transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                    className="relative z-10 w-full max-w-3xl h-[85vh] mb-[-2px] bg-[#0A0A0C] border-t border-x border-white/10 rounded-t-[32px] shadow-2xl overflow-hidden flex flex-col"
                >
                    {/* Top Handle for mobile */}
                    <div className="w-12 h-1.5 bg-white/20 rounded-full mx-auto mt-3 mb-1 sm:hidden" />

                    {/* Header */}
                    <div className="p-6 pb-4 border-b border-white/5 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-2xl">
                                <Coffee size={22} />
                            </div>
                            <div>
                                <h3 className="text-xl font-display font-bold text-white">Support ScreenRecord.ing</h3>
                                <p className="text-xs text-txt-dim">Keep ScreenRecord.ing free, open, and fast!</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-txt-dim hover:text-white hover:bg-white/10 rounded-full transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Content Body */}
                    <div className="p-6 overflow-y-auto space-y-6 flex-1 scrollbar-none">
                        {/* Intro Box */}
                        <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-2xl text-xs text-amber-200/90 leading-relaxed flex items-start gap-3">
                            <Heart size={18} className="text-amber-400 shrink-0 mt-0.5" />
                            <div>
                                <strong className="text-white font-semibold block mb-0.5">100% Free & Open Source</strong>
                                ScreenRecord.ing is maintained with love. If it saves you time or helps your work, consider buying us a coffee to support development!
                            </div>
                        </div>

                        {/* Tier Selection */}
                        <div className="space-y-2">
                            <label className="text-xs font-semibold text-white">Select Contribution</label>
                            <div className="grid grid-cols-3 gap-3">
                                {[
                                    { amount: 3, label: '☕ Espresso' },
                                    { amount: 5, label: '☕ Cappuccino' },
                                    { amount: 10, label: '📦 Coffee Box' }
                                ].map(tier => (
                                    <button
                                        key={tier.amount}
                                        onClick={() => setSelectedTier(tier.amount)}
                                        className={`p-3 rounded-2xl border flex flex-col items-center justify-center gap-1 transition-all ${
                                            selectedTier === tier.amount
                                                ? 'bg-amber-500/20 border-amber-500 text-white scale-105 shadow-lg shadow-amber-500/10'
                                                : 'bg-white/5 border-white/10 text-txt-dim hover:bg-white/10 hover:text-white'
                                        }`}
                                    >
                                        <span className="text-sm font-bold text-amber-400">${tier.amount}</span>
                                        <span className="text-[10px] font-medium">{tier.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Direct Links */}
                        <div className="space-y-3">
                            <a
                                href={`https://buymeacoffee.com/rjarivi?amount=${selectedTier}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full py-3.5 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-2xl text-xs shadow-lg shadow-amber-500/20 transition-all flex items-center justify-center gap-2"
                            >
                                <Coffee size={16} /> Buy Me a Coffee (${selectedTier}) <ExternalLink size={12} />
                            </a>

                            <a
                                href="https://github.com/sponsors/rjarivi"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full py-3 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-2xl text-xs border border-white/10 transition-all flex items-center justify-center gap-2"
                            >
                                <Heart size={16} className="text-rose-400" /> Sponsor on GitHub <ExternalLink size={12} />
                            </a>
                        </div>

                        {/* Crypto / Copyable Addresses */}
                        <div className="pt-4 border-t border-white/5 space-y-3">
                            <label className="text-xs font-semibold text-white">Direct Wallet Addresses</label>

                            <div className="p-3 bg-surface-900 border border-white/10 rounded-2xl flex items-center justify-between gap-3 text-xs">
                                <div>
                                    <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider block">ETH / EVM</span>
                                    <span className="font-mono text-[11px] text-white/90 truncate max-w-[240px] block">0x7593D3BA...7c91</span>
                                </div>
                                <button
                                    onClick={() => handleCopy('0x7593D3BA28e1C9e144654f6604008135c5aa7c91', 'ETH')}
                                    className="p-2 bg-white/5 hover:bg-white/10 text-txt-dim hover:text-white rounded-xl transition-colors shrink-0"
                                >
                                    {copiedField === 'ETH' ? <Check size={14} className="text-success-400" /> : <Copy size={14} />}
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    )
}
